import React from 'react';
import { useTranslation } from 'next-i18next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import cn from 'classnames';

import { Icon, IconButton, Link } from 'components';
import { getLayout } from 'components/layouts/Header';
import { CVUploadModalInstance } from 'components/candidate/CVUploadModal';
import { MatchesNotificationModal } from 'components/candidate/MatchesNotificationModal/MatchesNotificationModal.component';
import SignOutModalInstance from 'components/candidate/SignOutModal/SignOutModal.component';
import DeleteAccountModalInstance from 'components/candidate/DeleteAccountModal/DeleteAccountModal.component';
import { useResponsive } from 'hooks/useResponsive';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { withAuthCandidate } from 'lib/hocs/withAuthCandidate';
import { useCandidateMatches } from 'lib/providers/CandidateMatchesProvider';
import { PRIVACY_POLICY, TERMS_OF_USE } from 'utils/urls';

import styles from './Settings.module.scss';

const Settings: React.FC = () => {
  const screens = useResponsive();
  const router = useRouter();
  const { matchesMeta } = useCandidateMatches();
  const unvisited_count = matchesMeta?.unvisited_count;
  const { updateUserApiResponse } = useAuth();
  const [tCommon] = useTranslation('common');

  const handleCVUpload = async () => {
    try {
      await CVUploadModalInstance();
      updateUserApiResponse();
    } catch (error) {
      console.error(error);
    }
  };

  const openSignOutModal = async () => {
    try {
      await SignOutModalInstance();
    } catch (error) {
      console.error(error);
    }
  };

  const openDeleteAccountModal = async () => {
    try {
      await DeleteAccountModalInstance();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Head>
        <title>Settings | Huzzle</title>
        <meta name="description" content="Huzzle" />
      </Head>
      <div className={styles.Settings}>
        <div className={styles.wrapper}>
          <div className={styles.header}>
            {screens.sm && (
              <IconButton
                iconName="arrow-button-left"
                className={styles['left-nav-icon']}
                onClick={() => router.back()}
              />
            )}
            <p>{tCommon('settings')}</p>
          </div>
          <div className={styles.content}>
            <div className={styles['list-container']}>
              <div
                className={styles['list-item']}
                onClick={() => router.push(PAGE_ROUTES.CANDIDATE_MATCHES)}
              >
                <div className={styles['title-wrapper']}>
                  <p className={styles.title}>{tCommon('matches')}</p>
                  {unvisited_count && (
                    <div className={styles.count}>
                      <p>{unvisited_count}</p>
                    </div>
                  )}
                </div>
                <Icon
                  iconName="arrow-button-right"
                  className={styles['right-icon']}
                />
              </div>
              <div className={styles.divider}></div>
              <div
                className={styles['list-item']}
                onClick={() =>
                  router.push(
                    `${PAGE_ROUTES.CANDIDATE_ONBOARDING}?matching_preference=true`
                  )
                }
              >
                <p className={styles.title}>
                  {tCommon('matching-preferences')}
                </p>
                <Icon
                  iconName="arrow-button-right"
                  className={styles['right-icon']}
                />
              </div>
              <div className={styles.divider}></div>
              <div
                className={styles['list-item']}
                onClick={() => MatchesNotificationModal()}
              >
                <p className={styles.title}>
                  {tCommon('matching-notifications')}
                </p>
                <Icon
                  iconName="arrow-button-right"
                  className={styles['right-icon']}
                />
              </div>
            </div>
            <div className={styles['list-container']}>
              <div className={styles['list-item']} onClick={handleCVUpload}>
                <p className={styles.title}>CV</p>
                <Icon
                  iconName="arrow-button-right"
                  className={styles['right-icon']}
                />
              </div>
              <div className={styles.divider}></div>
              <div
                className={styles['list-item']}
                onClick={() => router.push(PAGE_ROUTES.CANDIDATE_PROFILE)}
              >
                <p className={styles.title}>{tCommon('edit-profile')}</p>
                <Icon
                  iconName="arrow-button-right"
                  className={styles['right-icon']}
                />
              </div>
            </div>
            <div className={styles['list-container']}>
              <div
                className={styles['list-item']}
                onClick={() => router.push(TERMS_OF_USE)}
              >
                <p className={styles.title}>Terms of use</p>
                <Icon
                  iconName="arrow-button-right"
                  className={styles['right-icon']}
                />
              </div>
              <div className={styles.divider}></div>
              <div
                className={styles['list-item']}
                onClick={() => router.push(PRIVACY_POLICY)}
              >
                <p className={styles.title}>Data privacy</p>
                <Icon
                  iconName="arrow-button-right"
                  className={styles['right-icon']}
                />
              </div>
            </div>
            <div className={styles['list-container']}>
              <Link href="mailto:hello@huzzle.app" underlineOnHover={false}>
                <div
                  className={styles['list-item']}
                  onClick={() => router.push(TERMS_OF_USE)}
                >
                  <p className={styles.title}>Contact us</p>
                  <Icon
                    iconName="arrow-button-right"
                    className={styles['right-icon']}
                  />
                </div>
              </Link>
            </div>
            <div className={styles['list-container']}>
              <div className={styles['list-item']} onClick={openSignOutModal}>
                <p className={styles.title}>{tCommon('sign-out')}</p>
              </div>
              <div className={styles.divider}></div>
              <div
                className={styles['list-item']}
                onClick={openDeleteAccountModal}
              >
                <p className={cn(styles.title, styles.danger)}>
                  {tCommon('delete-account')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'matches',
        'common',
        'candidate.settings',
      ])),
    },
  };
}

const SettingsWrapper = withAuthCandidate(Settings);
// @ts-ignore
SettingsWrapper.getLayout = getLayout;

export default SettingsWrapper;
